import { ofetch } from 'ofetch';

/**
 * 内存中的 accessToken（不落地 localStorage）
 * 由 authStore 登录/刷新后调用 setAccessToken 注入
 */
let inMemoryAccessToken: string | null = null;
export function setAccessToken(token: string | null) {
  inMemoryAccessToken = token;
}
export function getAccessToken(): string | null {
  return inMemoryAccessToken;
}

function toCamelCase(obj: unknown): unknown {
  if (obj === null || obj === undefined) return obj;
  if (Array.isArray(obj)) return obj.map((item) => toCamelCase(item));
  if (typeof obj !== 'object' || obj instanceof Date || obj instanceof File || obj instanceof FormData) return obj;
  const result: Record<string, unknown> = {};
  for (const key of Object.keys(obj as Record<string, unknown>)) {
    const camelKey = key.replace(/_([a-z])/g, (_, letter: string) => letter.toUpperCase());
    result[camelKey] = toCamelCase((obj as Record<string, unknown>)[key]);
  }
  return result;
}

function setHeader(options: Record<string, unknown>, key: string, value: string) {
  const headers = options.headers;
  if (headers instanceof Headers) {
    headers.set(key, value);
  } else if (headers && typeof headers === 'object') {
    (headers as Record<string, string>)[key] = value;
  } else {
    options.headers = { [key]: value };
  }
}

/**
 * 跨标签页单飞。同源开几个标签页就有几份 JS 上下文，下面的 `refreshInflight` 互不相见，
 * 两边同时到期就会把同一个 refreshToken 发出去两次 —— 服务端按「令牌复用」吊销整条令牌族，
 * 表现是偶发被踢回登录页（见 docs/接口契约.md）。
 *
 * 只需要**串行**，不需要跨标签页共享结果：HttpOnly cookie 是整个源共享的，后拿到锁的那次
 * 发的已经是前一次轮换出来的新 cookie，链式轮换天然命中不了复用检测。
 * `navigator.locks` 仅在安全上下文可用（https 与 localhost），拿不到时退回进程内单飞。
 */
const REFRESH_LOCK_NAME = 'yuyue:auth-refresh';
async function withRefreshLock<T>(task: () => Promise<T>): Promise<T> {
  const locks = typeof navigator === 'undefined' ? undefined : navigator.locks;
  if (!locks) return task();
  return locks.request(REFRESH_LOCK_NAME, task);
}

let refreshInflight: Promise<string | null> | null = null;
async function doRefresh(): Promise<string | null> {
  if (refreshInflight) return refreshInflight;
  refreshInflight = (async () => {
    try {
      return await withRefreshLock(refreshOnce);
    } catch {
      return null;
    } finally {
      refreshInflight = null;
    }
  })();
  return refreshInflight;
}

async function refreshOnce(): Promise<string | null> {
  try {
    const resp = await fetch('/api/auth/refresh', {
      method: 'POST',
      credentials: 'include',
    });
    if (!resp.ok) return null;
    const json = await resp.json();
    const data = json?.data;
    if (!data?.accessToken) return null;
    inMemoryAccessToken = data.accessToken as string;
    if (data.user && onRefreshedUserCallback) {
      try {
        onRefreshedUserCallback(data.user);
      } catch {
        /* ignore */
      }
    }
    return data.accessToken as string;
  } catch {
    return null;
  }
}

let onRefreshedUserCallback: ((user: unknown) => void) | null = null;
export function registerRefreshedUserCallback(cb: (user: unknown) => void) {
  onRefreshedUserCallback = cb;
}

/**
 * 暴露给 authStore 使用的 refresh 函数。
 *
 * 启动期恢复会话**必须走这里而不是自己 fetch**：`/auth/refresh` 每成功一次就把旧令牌
 * 轮换掉并吊销，同一条 refreshToken 发两次就是服务端眼里的「令牌复用」，整条令牌族会被吊销，
 * 而且后到的那次还会顺带把刚下发的新 cookie 清掉——表现就是刷新页面后偶发被踢回登录页。
 * 走这一条，hydrate 与 401 重放（以及 StrictMode 的双挂载）共享同一个在途请求。
 */
export async function refreshAccessToken(): Promise<string | null> {
  return doRefresh();
}

let onUnauthorizedCallback: (() => void) | null = null;
export function registerUnauthorizedCallback(cb: () => void) {
  onUnauthorizedCallback = cb;
}

/** 登录/刷新/登出端点自身的 401 不算「会话过期」，不该据此去刷新或清会话 */
const AUTH_PATH_PATTERN = /\/auth\/(login|refresh|logout)/;

function httpStatusOf(error: unknown): number | undefined {
  const ctx = error as { status?: number; response?: { status?: number } } | null;
  return ctx?.status ?? ctx?.response?.status;
}

/**
 * 命中 401 时静默刷新一次令牌：true 表示已换到新令牌，调用方可以重放原请求。
 *
 * 刷新失败就是会话终结——内存令牌清掉并通知 authStore 走登出，返回 false。
 * 走 doRefresh() 这一条单飞，所以同屏多个查询同时 401 也只会发出一条 /auth/refresh。
 */
async function recoverSession(path: string, error: unknown): Promise<boolean> {
  if (AUTH_PATH_PATTERN.test(path) || httpStatusOf(error) !== 401) return false;
  if (await doRefresh()) return true;
  inMemoryAccessToken = null;
  if (onUnauthorizedCallback) {
    try {
      onUnauthorizedCallback();
    } catch {
      /* ignore */
    }
  }
  return false;
}

const apiClient = ofetch.create({
  baseURL: '/api',
  credentials: 'include',
  headers: {
    'Content-Type': 'application/json',
  },
  onRequest({ options }) {
    const opts = options as unknown as Record<string, unknown>;
    if (inMemoryAccessToken) {
      setHeader(opts, 'Authorization', `Bearer ${inMemoryAccessToken}`);
    }
    const body = opts.body;
    if (body instanceof FormData || body instanceof Blob || body instanceof ArrayBuffer) {
      const headers = opts.headers;
      if (headers instanceof Headers) {
        headers.delete('Content-Type');
      } else if (headers && typeof headers === 'object') {
        delete (headers as Record<string, string>)['Content-Type'];
      }
    }
  },
  onResponse({ response }) {
    try {
      const json = response._data;
      if (json && typeof json === 'object') {
        response._data = toCamelCase(json);
      }
    } catch {
      /* ignore */
    }
  },
});

interface ApiError extends Error {
  status?: number;
}

function unwrapResponse<T>(raw: unknown): T {
  if (raw && typeof raw === 'object' && 'success' in raw) {
    const typed = raw as { success: boolean; data?: unknown; message?: unknown; pagination?: unknown; total?: unknown };
    if (typed.success === false) {
      const err: ApiError = new Error(
        typeof typed.message === 'string' && typed.message.trim() ? typed.message : '操作失败',
      );
      // 业务失败那句中文已经在 message 里，且这条错误没有 response/status（2xx 包着失败）。
      // 打个标记，免得下面那层「没有响应就换成网络兜底」把服务端真原因盖成「网络连接失败」。
      Object.assign(err, { isBusinessError: true });
      throw err;
    }
    if ('data' in typed && !('pagination' in typed) && !('total' in typed)) {
      return typed.data as T;
    }
  }
  return raw as T;
}

/**
 * 请求**根本没拿到响应**时的中文兜底（服务没起、断网、DNS 失败、被浏览器同源策略挡下）。
 *
 * 这类失败不经过 `onResponseError`，ofetch 把浏览器的 `TypeError: Failed to fetch` 包成 FetchError
 * 抛出，message 长成 `[GET] "/admin/api/…": <no response> Failed to fetch`；页面清一色
 * `e instanceof Error ? e.message : '…'`，于是英文原文带整串路径直接铺进界面。
 * 判据是「没有 response、没有 status、也不是业务失败」——凡服务端写过话的形状都带其一，不会被盖掉。
 * 原始串留在 `rawMessage`，排查时还看得到到底连的是哪个请求。
 */
const NETWORK_FAILURE_MESSAGE = '网络连接失败，请检查网络后重试';

function withNetworkFallback(error: unknown): unknown {
  if (!(error instanceof Error)) return error;
  const ctx = error as {
    response?: unknown;
    status?: number;
    isBusinessError?: boolean;
    code?: string;
    rawMessage?: string;
  };
  if (ctx.response !== undefined || typeof ctx.status === 'number' || ctx.isBusinessError === true) {
    return error;
  }
  ctx.rawMessage = error.message;
  error.message = NETWORK_FAILURE_MESSAGE;
  ctx.code = 'NETWORK_ERROR';
  return error;
}

/**
 * 裸 fetch 的传输层失败同样收口：文件出口走的是原生 `fetch`，没有 `onResponseError` 那层翻译。
 */
async function fetchOrNetworkFallback(input: string, init: RequestInit): Promise<Response> {
  try {
    return await fetch(input, init);
  } catch (error) {
    throw withNetworkFallback(error);
  }
}

/**
 * 4xx/5xx 也播报服务端那句中文。
 *
 * ofetch 抛的是 FetchError，`.message` 只有状态文本（"Bad Request"），服务端写的句子在 `.data.message`
 * 里；而各页面清一色 `e instanceof Error ? e.message : '…'` 直接显示。不接这一手，后端所有
 * 形状校验（值域、长度、必须提交）在界面上都只会剩 "Bad Request" 三个字，用户改不了任何东西。
 * 服务端没给 message（网关超时、HTML 错误页）时按原形状抛出，只有「压根没拿到响应」才换成网络兜底，
 * 状态码与原始错误都不吞。
 */
function withServerMessage(error: unknown): never {
  const ctx = error as { data?: { message?: unknown }; response?: { status?: number } } | null;
  const message = typeof ctx?.data?.message === 'string' && ctx.data.message.trim() ? ctx.data.message : null;
  if (!message) throw withNetworkFallback(error);
  const err: ApiError = new Error(message);
  err.status = ctx?.response?.status;
  throw err;
}

/**
 * 发一次请求，401 时「刷新 + 重放」一次再把结果交出去。
 *
 * 重放必须写在这一层，不能写在 ofetch 的 `onResponseError` 钩子里：ofetch 对 4xx/5xx 的处理是
 * `await callHooks(ctx, options.onResponseError)` 之后**恒**执行 `return await onError(ctx)`
 * （见 node_modules/ofetch/dist/shared/ofetch.*.mjs），钩子的返回值整个丢弃、原 Promise 照抛。
 * 于是历史上那段钩子代码把重放请求真发出去了（网络面板里能看到它 200），调用方拿到的却仍是 401——
 * 表现是 accessToken 一到期（登录后 15 分钟），每个查询的首个请求都在界面上报「未登录或登录已过期」，
 * 用户手动重试才好，而且看不出原因。
 */
async function request<T>(path: string, options: Parameters<typeof apiClient>[1]): Promise<T> {
  try {
    return unwrapResponse<T>(await apiClient(path, options));
  } catch (error) {
    if (!(await recoverSession(path, error))) return withServerMessage(error);
    // 不自己拼 Authorization：onRequest 会从内存里读到刚换出的新令牌
    try {
      return unwrapResponse<T>(await apiClient(path, options));
    } catch (retryError) {
      return withServerMessage(retryError);
    }
  }
}

export async function get<T>(path: string, params?: Record<string, unknown>): Promise<T> {
  return request<T>(path, { method: 'GET', params });
}

export async function post<T>(path: string, data?: unknown): Promise<T> {
  return request<T>(path, { method: 'POST', body: data as BodyInit });
}

export async function put<T>(path: string, data?: unknown): Promise<T> {
  return request<T>(path, { method: 'PUT', body: data as BodyInit });
}

export async function patch<T>(path: string, data?: unknown): Promise<T> {
  return request<T>(path, { method: 'PATCH', body: data as BodyInit });
}

export async function del<T>(path: string): Promise<T> {
  return request<T>(path, { method: 'DELETE' });
}

/**
 * 拉取受 JWT 保护的静态文件（身份证照片 / 付款凭证）或附件导出，返回可直接用于 img src 的 Blob URL。
 *
 * 后端只认 Authorization 请求头，不支持 `?token=` 查询串——令牌进 URL 会落到
 * 反向代理访问日志与浏览器历史。调用方用完后需自行 URL.revokeObjectURL 释放。
 * 401 时静默刷新一次令牌再重试，与 apiClient 的重放行为一致。
 */
async function fetchProtectedResponse(url: string): Promise<Response> {
  const request = (token: string | null) =>
    fetchOrNetworkFallback(url, {
      credentials: 'include',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });

  let resp = await request(inMemoryAccessToken);
  if (resp.status === 401) {
    const newToken = await doRefresh();
    if (!newToken) throw new Error('未授权或登录已过期');
    resp = await request(newToken);
  }
  if (!resp.ok) throw new Error(await readErrorMessage(resp));
  return resp;
}

/**
 * 非 2xx 的响应体是 `{ message: '中文说明' }`，直连 fetch 不走 apiClient 那层翻译，
 * 不自己取出来的话界面上就只剩「文件获取失败 (400)」，而 400 的真实原因往往是
 * 「这一年还没有归档报表」这种用户能自己纠正的话。
 */
async function readErrorMessage(resp: Response): Promise<string> {
  const fallback = `文件获取失败 (${resp.status})`;
  try {
    const json = (await resp.clone().json()) as { message?: unknown } | null;
    const message = typeof json?.message === 'string' ? json.message.trim() : '';
    return message || fallback;
  } catch {
    return fallback;
  }
}

export async function fetchProtectedObjectUrl(url: string): Promise<string> {
  return URL.createObjectURL(await (await fetchProtectedResponse(url)).blob());
}

/** 导出附件（如年度台账 .xlsx）：带令牌拉成 Blob 后按指定文件名触发下载。 */
export async function downloadProtectedFile(url: string, filename: string): Promise<void> {
  const objectUrl = URL.createObjectURL(await (await fetchProtectedResponse(url)).blob());
  const anchor = document.createElement('a');
  anchor.href = objectUrl;
  anchor.download = filename;
  anchor.click();
  // 点下即刻 revoke 会让部分浏览器把还没起步的下载取消掉，留给它们一个足够读完 Blob 的时间窗
  window.setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
}